export const PRESENTATION_LIMITS = {
  /** Minimum topic length - ensures enough context for AI to generate meaningful content */
  MIN_DESCRIPTION_CHARS: 10,
  /** Maximum topic length - users should be able to paste in a transcript, spec, long details, etc. */
  MAX_DESCRIPTION_CHARS: 50_000,
  /** Maximum slides per presentation - UX limit to keep presentations manageable */
  MAX_SLIDES: 100,
  /** Minimum plan length from AI - ensures structured output has enough detail */
  MIN_PLAN_CHARS: 100,
  /** Maximum plan length - prevents runaway AI generation; ~12k words is plenty for any deck */
  MAX_PLAN_CHARS: 50_000,
} as const;

export const DEMO_DECK_LIMITS = {
  /** Number of demo deck slots shown to a user in the new presentation wizard */
  SLOT_COUNT: 4,
} as const;

export const GENERATOR_PROGRESS_LIMITS = {
  ACTIVE_SESSION_MAX_AGE_MS: 9 * 60 * 60 * 1000,
  ACTIVE_JOB_RECONNECT_MAX_AGE_MS: 9 * 60 * 60 * 1000,
  STRUCTURE_HANDOFF_TIMEOUT_SECONDS: 45,
  FINALIZING_STALL_SECONDS: 120,
  FINALIZATION_OPEN_TIMEOUT_SECONDS: 45,
  DETERMINISTIC_JOB_START_GRACE_SECONDS: 30,
} as const;

export const GENERATION_RECOVERY_FLAGS = {
  ENABLE_LOCAL_STORAGE_ACTIVE_GENERATION_RECOVERY:
    process.env.NEXT_PUBLIC_ENABLE_ACTIVE_GENERATION_RECOVERY !== 'false',
} as const;

export const RENDER_QUEUE_FLAGS = {
  /**
   * When an edit changes the inputs of a queued render, pause that work
   * (mark-stale) and ask the editor whether to update or remove it.
   * Temporarily off while that flow is buggy. The server's completion-time
   * input check still marks outdated work "Needs update" when it finishes.
   */
  ENABLE_PENDING_RENDER_CHANGE_NOTICE: false,
} as const;

/**
 * Tunable knobs for real-time collaboration. Designed for up to 10 simultaneous
 * editors and optimized for ~3. Cursor writes are throttled and presence is kept
 * fresh by a heartbeat; viewers drop sessions whose `lastSeen` is older than the
 * stale threshold.
 */
export const COLLAB_LIMITS = {
  /** Plan-for ceiling of simultaneous editors. */
  MAX_EDITORS: 10,
  /** Sweet-spot editor count the UX is tuned for. */
  OPTIMIZED_EDITORS: 3,
  /** Minimum ms between broadcast cursor writes (~12.5 Hz). */
  CURSOR_THROTTLE_MS: 80,
  /** Heartbeat cadence to keep a presence record fresh. */
  PRESENCE_HEARTBEAT_MS: 5_000,
  /** A presence record older than this (no heartbeat) is treated as gone. */
  PRESENCE_STALE_MS: 15_000,
  /** Idle (no pointer/keyboard activity) after this is reflected as state:'idle'. */
  PRESENCE_IDLE_MS: 60_000,
  /** Debounce before an edit triggers render-input dirty detection. */
  EDIT_FLUSH_DEBOUNCE_MS: 350,
  /** Fixed coalescing window for rapid focused-field collaboration writes. */
  FOCUSED_FIELD_SYNC_INTERVAL_MS: 90,
  /**
   * Window after a local element write during which the reconciler protects that
   * element from remote overwrite — covers write latency + snapshot propagation
   * so a stale self-snapshot can't revert the optimistic edit before the server
   * confirms it. After this, peers' last-writer-wins updates apply normally.
   */
  RECENT_WRITE_PROTECT_MS: 4_000,
  /** How often a connected client pokes the render-queue worker tick. */
  RENDER_QUEUE_TICK_MS: 2_000,
  /** Max render jobs a single tick will process before yielding. */
  RENDER_QUEUE_TICK_BATCH: 4,
} as const;

export const EXPORT_LIMITS = {
  /** Max video file size for PPTX embedding - balances quality with file size/memory */
  MAX_VIDEO_BYTES: 100 * 1024 * 1024,
  /** Max image file size - thumbnails/slides rarely need more than 50MB */
  MAX_IMAGE_BYTES: 50 * 1024 * 1024,
  /**
   * Maximum size of any single exported artifact (MP4, PPTX, ZIP).
   * 1.5GB is intentionally generous so long decks still work, but it is low
   * enough to keep tmpfs + upload memory use within Cloud Run limits.
   */
  MAX_EXPORT_FILE_BYTES: 1.5 * 1024 * 1024 * 1024,
  /**
   * Floor/ceiling for slide dwell time when exporting MP4.
   * The value 0 is allowed so an export can contain only transitions. Any
   * positive dwell time must be at least this floor to avoid invalid or
   * extremely short static-slide segments. The maximum prevents runaway
   * runtimes.
   */
  MIN_MP4_SLIDE_DURATION_MS: 1_000,
  MAX_MP4_SLIDE_DURATION_MS: 60_000,
  /**
   * Hard cap on total MP4 runtime. 20 minutes keeps exports manageable while
   * still covering very long decks (100 slides @12s each).
   */
  MAX_MP4_TOTAL_DURATION_MS: 20 * 60 * 1000,
  /** Default transition video duration - industry standard for smooth transitions */
  DEFAULT_TRANSITION_DURATION_SECS: 5,
  /** Fast transition duration - sped-up transitions for snappier playback */
  FAST_TRANSITION_DURATION_SECS: 3,
} as const;

export const PLAYBACK_LIMITS = {
  /**
   * Default slide dwell time in Present mode autoplay.
   * 2.5 seconds feels cinematic without being too slow.
   */
  PRESENT_MODE_SLIDE_DURATION_MS: 2_500,
  /**
   * Scheduling tolerance after a transition's declared duration before the
   * playback watchdog advances when the browser omits the ended event.
   */
  TRANSITION_WATCHDOG_GUARD_MS: 100,
  /**
   * How long player controls remain visible before auto-hiding during playback.
   * Matches the slide duration so controls fade by the time the first advance happens,
   * but these are independent knobs that can be tuned separately.
   */
  CONTROLS_HIDE_DELAY_MS: 2_500,
  /**
   * How long Present Mode's 'await-generation' open keeps reading the live
   * deck before freezing it anyway. This decides whether a presenter whose
   * deck never settles gets a frozen (bounded wait) or live-mutating deck --
   * a product feel decision. See
   * `~/lib/presentations/generator/present-mode-snapshot.ts` for the latch
   * mechanics.
   */
  PRESENT_SNAPSHOT_SETTLE_TIMEOUT_MS: 5_000,
} as const;

/** Transition speed options for export */
export type TransitionSpeed = 'fast' | 'normal';

export const RATE_LIMITS = {
  /** Structure generation - per-user limit; backend uses Fable 5 with GPT-5.5 fallback */
  GENERATE_STRUCTURE: { windowMs: 60_000, maxRequests: 100 },
  /** Design generation - per-user limit; backend uses gemini-3.1-flash-image (2K RPM API quota) */
  GENERATE_DECK: { windowMs: 60_000, maxRequests: 60 },
  /** A full 100-slide deck can submit up to 99 transition generations. */
  GENERATE_TRANSITION: {
    windowMs: 60_000,
    maxRequests: PRESENTATION_LIMITS.MAX_SLIDES,
  },
  /** Generator autosave - frequent writes but lightweight compared to full save */
  GENERATOR_AUTOSAVE: { windowMs: 60_000, maxRequests: 120 },
  /** Export operations - CPU/memory intensive, throttle to protect server */
  EXPORT: { windowMs: 60_000, maxRequests: 10 },
  /** Deck duplication - copies every deck object server-side; throttle like exports */
  PRESENTATION_DUPLICATE: { windowMs: 60_000, maxRequests: 10 },
  /** Wireframe rendering - CPU/memory intensive, throttle to protect server */
  RENDER_WIREFRAME: { windowMs: 60_000, maxRequests: 20 },
} as const;

export const COLOR_LIMITS = {
  /** Maximum colors in extracted palette - UX tested; 6 colors is visually digestible */
  MAX_PALETTE_SIZE: 6,
  /** Minimum LAB color distance from background - ensures text readability */
  MIN_DISTANCE_FROM_BACKGROUND: 15,
  /** Minimum LAB color distance between palette colors - ensures visual distinction */
  MIN_DISTANCE_FROM_OTHERS: 10,
  /** Quantization bits per channel - 5 bits = 32 levels, balances accuracy vs memory */
  QUANTIZE_BITS: 5,
  /** DeltaE threshold for merging similar colors - 10 is perceptually distinct */
  MERGE_THRESHOLD: 10,
} as const;

export const TRANSITION_LIMITS = {
  /** Max transition prompt length - balances expressiveness with video API limits */
  MAX_PROMPT_CHARS: 1_000,
} as const;

export const TRANSITION_CANVAS = {
  /** Canonical slide-media canvas width (px) for 16:9 transition video + poster. */
  WIDTH: 1920,
  /** Canonical slide-media canvas height (px) for 16:9 transition video + poster. */
  HEIGHT: 1080,
} as const;

export const POLLING_LIMITS = {
  /** Credit balance polling interval - 30s balances responsiveness with API load */
  BALANCE_POLL_INTERVAL_MS: 30_000,
} as const;

export const DOCUMENT_LIMITS = {
  /** Maximum file size for supporting documents (10MB) */
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  /** Maximum number of supporting documents per generation */
  MAX_DOCUMENTS: 10, // Increased from 5 - agentic exploration handles more docs
  /** Maximum total characters from all documents (agentic mode) */
  MAX_TOTAL_CHARS: 2_000_000, // Increased from 500K to 2M - on-demand retrieval
  /** Maximum characters per individual document */
  MAX_CHARS_PER_DOCUMENT: 500_000, // Increased from 200K to 500K
  /** Legacy limit for non-agentic mode (single-shot prompt stuffing) */
  MAX_TOTAL_CHARS_LEGACY: 500_000,
} as const;

export const AI_SDK_V6_FLAGS = {
  /** Use agentic planning for documents larger than this threshold */
  AGENTIC_PLANNING_THRESHOLD_CHARS: 50_000,
  /** Enable agentic planning (feature flag for gradual rollout) */
  ENABLE_AGENTIC_PLANNING: process.env.ENABLE_AGENTIC_PLANNING === 'true',
} as const;

export const AI_CONCURRENCY_LIMITS = {
  /** OpenAI concurrent requests - balances throughput with rate limits */
  OPENAI: 3,
  /** OpenAI image generation requests for best-quality slide imagery */
  OPENAI_IMAGE: 24,
  /** Anthropic Claude Fable 5 text-only requests */
  ANTHROPIC_TEXT: 2,
  /** Anthropic Claude Fable 5 multimodal text requests */
  ANTHROPIC_MULTIMODAL: 2,

  // Gemini 3 Flash (gemini-flash-latest, gemini-3.1-flash-*) - 20K RPM quota
  /** Gemini 3 Flash text-only requests */
  GEMINI_3_FLASH_TEXT: 5,
  /** Gemini 3 Flash multimodal (text + images) requests */
  GEMINI_3_FLASH_MULTIMODAL: 4,
  /** Gemini 3.1 Flash image generation (gemini-3.1-flash-image, Nano Banana 2) */
  GEMINI_3_FLASH_IMAGE: 24,

  // Gemini 3 Pro (gemini-3-pro-*) - 2K RPM quota, compute intensive
  /** Gemini 3 Pro text-only requests */
  GEMINI_3_PRO_TEXT: 2,
  /** Gemini 3 Pro multimodal requests */
  GEMINI_3_PRO_MULTIMODAL: 2,
  /** Gemini 3 Pro image generation group */
  GEMINI_3_PRO_IMAGE: 2,

  // Gemini 2.5 (not currently used, preserved for future/fallback)
  /** Gemini 2.5 Flash text-only requests */
  GEMINI_2_5_FLASH_TEXT: 3,
  /** Gemini 2.5 Flash multimodal requests */
  GEMINI_2_5_FLASH_MULTIMODAL: 3,
  /** Gemini 2.5 Flash image generation */
  GEMINI_2_5_FLASH_IMAGE: 2,
  /** Gemini 2.5 Pro text-only requests */
  GEMINI_2_5_PRO_TEXT: 2,
  /** Gemini 2.5 Pro multimodal requests */
  GEMINI_2_5_PRO_MULTIMODAL: 2,
} as const;

export const AI_START_RATE_LIMITS = {
  /** OpenAI image generation starts per minute; keeps bursty best-image runs below provider limits */
  OPENAI_IMAGE: 24,
  /** Gemini 3.1 Flash image starts per minute; keeps bursty draft-image runs below provider limits */
  GEMINI_3_FLASH_IMAGE: 24,
} as const;

/** Default quality for slide design and magic-edit image generation. */
export const IMAGE_QUALITY_DEFAULT = 'best' as const;

/** Default quality for cinematic transition video generation. */
export const DEFAULT_VIDEO_QUALITY = 'best' as const;

export const IMAGE_GENERATION_CREDITS = {
  draft: 6,
  best: 25,
} as const;

export const GENERATOR_LIMITS = {
  /** Lower bound for runtime-configured design fanout */
  MIN_CONCURRENT_DESIGN_CALLS: 1,
  /** Upper bound for runtime-configured design fanout */
  MAX_CONCURRENT_DESIGN_CALLS: 24,
  /** Max concurrent design API calls from client - should not exceed server's GEMINI_IMAGE limit */
  MAX_CONCURRENT_DESIGN_CALLS_DRAFT: 6,
  /** Best-image design path should not exceed server's OPENAI_IMAGE limit */
  MAX_CONCURRENT_DESIGN_CALLS_BEST: 6,
  /** Slide-description redesign path should not exceed default server OPENAI text concurrency */
  MAX_CONCURRENT_SLIDE_DESCRIPTION_CALLS: AI_CONCURRENCY_LIMITS.OPENAI,
  /** Keep one client batch within Kie's shared 20-submissions-per-10-seconds account gate. */
  MAX_CONCURRENT_KIE_SUBMISSIONS: 20,
  /** Timeout guards for redesign operations to avoid hanging UI */
  REDESIGN_TIMEOUT_MS: {
    FONT_PRELOAD: 5_000,
    WAIT_FOR_SLIDE: 1_500,
    CAPTURE: 8_000,
    REQUEST: 85_000,
    WATCHDOG_BUFFER: 5_000,
  },
  /** Milliseconds between elapsed-time progress ticks while redesigning slides */
  REDESIGN_PROGRESS_TICK_MS: 500,
  /** Active redesign glow timing contract */
  REDESIGN_GLOW_MS: 1_000,
  REDESIGN_GLOW_FADE_MS: 200,
} as const;

export const ASYNC_JOB_LIMITS = {
  /** How long export files remain downloadable (hours) */
  EXPORT_URL_EXPIRY_HOURS: 24,
  /** Job timeout before marking as failed (ms) */
  JOB_TIMEOUT_MS: 5 * 60 * 1000,
  /** Poll cadence while waiting for processed transition videos to become export-ready. */
  EXPORT_READINESS_POLL_INTERVAL_MS: 1_000,
  /** Maximum time to keep the production monitor open while export finalization catches up. */
  EXPORT_READINESS_WAIT_TIMEOUT_MS: 60 * 1000,
  /**
   * Extended timeout for heavy MP4 exports; Cloud Run is configured to allow
   * long-running ffmpeg work, so we allow jobs to live up to 30 minutes.
   */
  MP4_JOB_TIMEOUT_MS: 30 * 60 * 1000,
  /** Storage path prefix for exports */
  EXPORT_STORAGE_PREFIX: 'exports',
} as const;

export const UPLOAD_LIMITS = {
  /** Binary image selected for a slide element; leaves HTTP ingress headroom. */
  MAX_SLIDE_ELEMENT_IMAGE_BYTES: 24 * 1024 * 1024,
  /**
   * Maximum PPTX file size for upload (100MB).
   * With signed URL flow, we bypass Cloud Run's 32MB request body limit.
   * 100MB accommodates large presentations with embedded media.
   */
  MAX_PPTX_SIZE: 100 * 1024 * 1024,
  /**
   * Signed URL expiry time (15 minutes).
   * Short enough to prevent URL sharing/abuse, long enough for slow connections.
   */
  SIGNED_URL_EXPIRY_MS: 15 * 60 * 1000,
} as const;

export const VIDEO_PROCESSING_LIMITS = {
  /**
   * Maximum concurrent ffmpeg post-processing operations per instance.
   * Set to 1 for effectively serial processing (safest with 1 CPU).
   * Can increase to 2-3 if CPU is increased to 2+ cores.
   */
  MAX_CONCURRENT_PROCESSING: 1,
  /**
   * Threshold for detecting stale processing jobs (ms).
   * Jobs stuck in 'processing' status longer than this are considered stale
   * and will be reset to 'failed' so users can retry.
   * 10 minutes allows for slow Cloud Run instances while catching crashed jobs.
   */
  STALE_THRESHOLD_MS: 10 * 60 * 1000,
  /** Interval for checking stale jobs on client side (ms) */
  STALE_CHECK_INTERVAL_MS: 30 * 1000,
} as const;

export const MP4_EXPORT_LIMITS = {
  /**
   * Maximum concurrent MP4 exports per Cloud Run instance.
   * Keep at 1 to avoid ffmpeg contention and tmpfs pressure.
   */
  MAX_CONCURRENT_EXPORTS: 1,
  /**
   * Soft ceiling for /tmp usage during export. We batch/clean aggressively to
   * stay under 1.5GB even on large decks.
   */
  MAX_TEMP_DISK_BYTES: 1.5 * 1024 * 1024 * 1024,
  /**
   * How many segments to accumulate before an intermediate concat/cleanup pass.
   * Smaller batches reduce peak temp usage; 8–10 keeps ffmpeg overhead low.
   */
  BATCH_SIZE: 10,
  /**
   * How often to check disk usage while processing slides. Using a small
   * interval catches growth early without adding too many fs stats.
   */
  DISK_CHECK_INTERVAL: 5,
  /**
   * Use resumable uploads above this size to avoid wasting large renders on
   * transient network errors.
   */
  RESUMABLE_UPLOAD_THRESHOLD_BYTES: 100 * 1024 * 1024,
  /** Retry budget for download steps (with exponential backoff). */
  DOWNLOAD_MAX_RETRIES: 3,
  /** Base delay (ms) for download retry backoff. */
  DOWNLOAD_RETRY_BASE_DELAY_MS: 1_000,
} as const;

export const UPLOAD_CREDITS = {
  /** Credits per slide for PPTX upload (covers transition prompt generation) */
  PER_SLIDE: 1,
} as const;

export const VIDEO_GENERATION_LIMITS = {
  /**
   * Maximum time (in seconds) before a video generation is considered timed out.
   * Used by the progress bar to set the ceiling for the deceleration phase.
   * The server-side WEBHOOK_LIMITS.MAX_GENERATION_TIME_MS mirrors this value.
   *
   * 15 minutes provides headroom over the observed production max of ~12 min
   * while being tight enough to reject truly stale webhooks.
   */
  MAX_GENERATION_TIME_SECONDS: 15 * 60,
} as const;

export const WATERMARK = {
  /** Discount multiplier when watermark is enabled (50% = half price) */
  CREDIT_DISCOUNT: 0.5,
  /** localStorage key for tracking if user has seen the discount info popup */
  DISCOUNT_INFO_SEEN_KEY: 'dd-watermark-discount-seen',
  /** Watermark text to overlay on images */
  TEXT: 'DirectorDeck.com',
  /** Watermark opacity (0-1) */
  OPACITY: 0.7,
  /** Watermark color (dark gray for visibility on most backgrounds) */
  COLOR: '#4A5568',
  /** Padding from image edges in pixels (scaled proportionally) */
  PADDING_PX: 20,
} as const;

/**
 * Calculate the discounted credit cost when watermark is enabled.
 * Formula: Math.max(1, Math.floor(normalCost * WATERMARK.CREDIT_DISCOUNT))
 */
export function calculateWatermarkCost(normalCost: number): number {
  return Math.max(1, Math.floor(normalCost * WATERMARK.CREDIT_DISCOUNT));
}
/** Approved Signature offers. Existing Studio subscriptions keep their catalog terms. */
export const SIGNATURE_PRICING_LIMITS = {
  MONTHLY_SEAT_CENTS: 25_000,
  ANNUAL_SEAT_CENTS: 240_000,
  MONTHS_PER_YEAR: 12,
  CREDITS_PER_SEAT: 3_000,
  FOUNDER_SEATS: 1,
  TEAM_MIN_SEATS: 3,
  TEAM_MAX_SEATS: 49,
  ENTERPRISE_MIN_SEATS: 50,
  PROJECT_CREDITS: 380,
  PROJECT_SLIDES: 12,
  REVISION_ALLOWANCE_PERCENT: 25,
  TOP_UP_CREDITS: 1_000,
  TOP_UP_CENTS: 10_000,
  TOP_UP_EXPIRATION_DAYS: 60,
} as const;

export const MARKETING_LIMITS = {
  /** Bound the initial session check before embedded account creation. */
  SESSION_CHECK_TIMEOUT_MS: 5_000,
  MIN_STUDIO_SEATS: 3,
  /** Recognized retired Studio subscriptions retain their original floor. */
  LEGACY_MIN_STUDIO_SEATS: 1,
  MAX_STUDIO_SEATS: 99,
  STUDIO_SEAT_PRICE_CENTS: 19900,
} as const;

export const MARKETING_RENDER_LIMITS = {
  TIER_DPR: { high: [1, 2], low: 1 },
  TIER_SETTINGS: {
    high: { steps: 48, bloom: true },
    low: { steps: 16, bloom: false },
  },
} as const;
